import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { GeneralDatePicker, GeneralEditor, GeneralSelectMenu, GeneralUpload } from 'src/components/general';
import { FormProvider, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { networkCreateArtist, useFetchArtistTypeListV2 } from 'src/network/artists';
import useAppInfoStore from 'src/store/appInfo';
import useFormsModalStore from 'src/store/forms';
import { generateSelectMenuItems } from 'src/utils';
import { getDefaultValues } from './defaultValues';
import { schema } from './validationSchema';

const GameArtistForm = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { modal, closeModal } = useFormsModalStore();

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(null);

  const [artistTypeList, setArtistTypeList] = useState([]);
  const [selectedArtistType, setSelectedArtistType] = useState('');

  const gameWikiDetails = modal?.data;
  const defaultValues = getDefaultValues(gameWikiDetails);
  const isEditMode = gameWikiDetails;

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
    trigger,
    setValue,
  } = methods;

  const PAYLOAD = {
    ContentID: 20,
  };

  // ---------------------------------------------------------------

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      ArtistsIsActive: values?.ArtistsIsActive ? 1 : 0,
      ArtistsArtistsTypeID: selectedArtistType,
      ArtistsBDate: new Date(startDate).toISOString(),
      ArtistsDDate: new Date(endDate).toISOString(),
    };

    try {
      const res = await networkCreateArtist(PAYLOAD);
      if (res?.data?.items?.ResultID === -1) {
        enqueueSnackbar(res?.data?.items?.Desc, { variant: 'error' });
      } else {
        enqueueSnackbar('با موفقیت ثبت شد');
        reset();
        if (event.nativeEvent.submitter.name === 'save') {
          closeModal();
        }
        await queryClient.invalidateQueries({ queryKey: ['artist-type-list-V2', PAYLOAD] });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const { data, isLoading } = useFetchArtistTypeListV2(PAYLOAD);

  useEffect(() => {
    if (data) {
      const artistTypeList = data?.data?.items;
      const artistTypes = generateSelectMenuItems(artistTypeList, 'ArtistsTypeLable', 'ArtistsTypeID');
      setArtistTypeList(artistTypes);
      setSelectedArtistType(artistTypes?.[0]?.id);
    }
  }, [data]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                mb: 5,
              }}
            >
              {isLoading ? (
                <></>
              ) : (
                <>
                  <RHFTextField name="ArtistsLable" type="text" label="نام سازنده/عامل به فارسی" />

                  <RHFTextField name="ArtistsLabelEn" type="text" label="نام سازنده/عامل به انگلیسی" />

                  <GeneralUpload
                    label="تصویر"
                    id="artists_images"
                    name="ArtistsImages"
                    afterChangeAction={(res) => {
                      setValue('ArtistsImages', res);
                      trigger('ArtistsImages');
                    }}
                  />

                  <GeneralSelectMenu
                    label="نقش سازنده/عامل"
                    name="ArtistsArtistsTypeID"
                    options={artistTypeList}
                    value={selectedArtistType}
                    onChangeValue={setSelectedArtistType}
                  />

                  <GeneralDatePicker label="تاریخ تولد" name="ArtistsBDate" value={startDate} onChangeValue={setStartDate} />

                  <GeneralDatePicker
                    maxDate={new Date()}
                    label="تاریخ وفات"
                    name="ArtistsDDate"
                    value={endDate}
                    onChangeValue={setEndDate}
                  />
                </>
              )}
            </Box>

            <Box
              sx={{
                mt: 3,
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)' },
              }}
            >
              <RHFTextField name="ArtistsBody" multiline rows={6} label="توضیحات" />
              <RHFSwitch name="ArtistsIsActive" labelPlacement="end" label="آیا نمایش داده شود؟" />
            </Box>

            <div className="flex items-center gap-4 mt-6">
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
              </LoadingButton>
              <Button onClick={closeModal} variant="contained" color="error">
                انصراف
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default GameArtistForm;
